// Web Audio 메트로놈 엔진
//
// setInterval로 소리를 바로 내면 타이머 지연만큼 박이 밀리므로, 짧은 주기(25ms)로 깨어나
// "앞으로 lookahead초 안에 울릴 소리"를 오디오 클럭(ctx.currentTime)에 미리 예약한다.
// 화면 표시는 예약해 둔 박 목록을 requestAnimationFrame에서 오디오 시각에 맞춰 꺼내 쓴다.
//
// 설정(config)은 재생 중에도 바꿀 수 있고 다음 예약부터 반영된다.
//  bpm, beats, groups(묶음 배열), accents(박별 0~3), subdivision('1'|'2'|'3'|'4'|'swing'),
//  sound('click'|'wood'|'bell'|'beep'), voice('off'|'native'),
//  countIn(bool), trainer{on, every, step, target}, gap{on, play, mute}

const LOOKAHEAD = 0.1;
const LOOKAHEAD_HIDDEN = 1.5; // 백그라운드 탭은 타이머가 1초 단위로 늦춰지므로 더 멀리 예약한다
const TICK_MS = 25;
const ACCENT_GAIN = [0, 0.5, 0.75, 1];
const VOICE_GAIN = [0, 0.6, 0.8, 1];

// 음색별 [강, 중, 약] 주파수와 파형·여운
const SOUNDS = {
  click: { freqs: [1800, 1350, 1000], type: 'sine', decay: 0.045 },
  wood: { freqs: [1050, 850, 680], type: 'triangle', decay: 0.06, drop: 0.7 },
  bell: { freqs: [2100, 1580, 1250], type: 'sine', decay: 0.28, overtone: 2.76 },
  beep: { freqs: [1320, 990, 880], type: 'square', decay: 0.05 },
};

export default class MetronomeEngine {
  constructor() {
    this.ctx = null;
    this.config = null;
    this.playing = false;
    this.listeners = new Set();
    this.voice = null; // { buffer, clips: { native: [[start, dur]...] } }
    this.voiceLoading = null;
    this.queue = [];
    this.timer = null;
    this.raf = null;
  }

  setConfig(config) {
    this.config = config;
  }

  // 박 이벤트 구독: fn({ beat, bar, muted, countIn, voiceActive }) / fn({ type: 'bpm', bpm }) / fn({ type: 'stop' })
  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  emit(event) {
    this.listeners.forEach((fn) => fn(event));
  }

  ensureContext() {
    if (!this.ctx) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      this.ctx = new AudioCtx();
    }
    // iOS 17+: 무음 스위치가 켜져 있어도 재생되도록 미디어 재생 세션으로 지정
    try {
      if (navigator.audioSession) navigator.audioSession.type = 'playback';
    } catch (e) { /* 미지원 브라우저 */ }
    return this.ctx;
  }

  loadVoice() {
    if (this.voice) return Promise.resolve(this.voice);
    if (this.voiceLoading) return this.voiceLoading;
    const ctx = this.ensureContext();
    this.voiceLoading = Promise.all([
      fetch('/sounds/voice-ko.json').then((r) => r.json()),
      fetch('/sounds/voice-ko.wav').then((r) => r.arrayBuffer()),
    ])
      .then(([meta, data]) => new Promise((resolve, reject) => {
        // 구형 Safari는 Promise 형태 decodeAudioData를 지원하지 않아 콜백 형태로 호출한다
        ctx.decodeAudioData(data, (buffer) => resolve({ buffer, clips: meta.clips }), reject);
      }))
      .then((voice) => {
        this.voice = voice;
        return voice;
      })
      .catch((err) => {
        this.voiceLoading = null;
        throw err;
      });
    return this.voiceLoading;
  }

  // 이 템포에서 마디 안 모든 숫자 음성이 다음 박 전에 끝나면 음성을 쓴다 (아니면 클릭으로 대체)
  voiceFits(config = this.config) {
    if (!config || config.voice === 'off' || !this.voice) return false;
    const clips = this.voice.clips[config.voice];
    if (!clips) return false;
    const interval = 60 / config.bpm;
    const longest = Math.max(...clips.slice(0, config.beats).map(([, dur]) => dur));
    return longest <= interval * 1.05;
  }

  async start() {
    const ctx = this.ensureContext();
    await ctx.resume();
    if (this.config.voice !== 'off') {
      try { await this.loadVoice(); } catch (e) { /* 음성 로드 실패 시 클릭으로 진행 */ }
    }
    this.playing = true;
    this.step = 0;
    this.bar = 0;
    this.inCountIn = !!this.config.countIn;
    this.queue = [];
    this.nextTime = ctx.currentTime + 0.08;
    this.timer = setInterval(() => this.schedule(), TICK_MS);
    this.schedule();
    this.raf = requestAnimationFrame(() => this.draw());
  }

  stop() {
    if (!this.playing) return;
    this.playing = false;
    clearInterval(this.timer);
    cancelAnimationFrame(this.raf);
    this.queue = [];
    this.emit({ type: 'stop' });
  }

  stepsPerBeat() {
    const sub = this.config.subdivision;
    return sub === 'swing' ? 2 : Number(sub) || 1;
  }

  schedule() {
    if (!this.playing) return;
    const ctx = this.ctx;
    this.flush();
    // 화면이 꺼졌다 돌아오는 등으로 예약 시각이 한참 지나 있으면 몰아서 울리지 않고 현재로 맞춘다
    if (this.nextTime < ctx.currentTime - 0.2) this.nextTime = ctx.currentTime + 0.05;
    const horizon = ctx.currentTime + (document.hidden ? LOOKAHEAD_HIDDEN : LOOKAHEAD);

    while (this.nextTime < horizon) {
      const cfg = this.config;
      const per = this.stepsPerBeat();
      const beat = Math.floor(this.step / per);
      const sub = this.step % per;
      const spb = 60 / cfg.bpm;
      const gapCycle = cfg.gap?.on ? cfg.gap.play + cfg.gap.mute : 0;
      const muted = !this.inCountIn && gapCycle > 0 && this.bar % gapCycle >= cfg.gap.play;
      const level = this.inCountIn ? 2 : (cfg.accents[beat] ?? 1);

      if (!muted && level > 0) {
        if (sub === 0) {
          if (this.voiceFits(cfg)) this.playVoice(this.nextTime, beat, level);
          else this.playClick(this.nextTime, this.inCountIn ? 2 : level, false);
        } else if (!this.inCountIn) {
          this.playClick(this.nextTime, 1, true);
        }
      }
      if (sub === 0) {
        this.queue.push({ time: this.nextTime, beat, bar: this.bar, muted, countIn: this.inCountIn });
      }

      let duration = spb / per;
      if (cfg.subdivision === 'swing') duration = sub === 0 ? (spb * 2) / 3 : spb / 3;
      this.nextTime += duration;
      this.step++;

      // 예비 박은 박 단위로만 센다 (세분 없이)
      if (this.inCountIn && sub === 0 && per > 1) {
        this.nextTime += spb - duration;
        this.step += per - 1;
      }

      if (this.step >= per * cfg.beats) {
        this.step = 0;
        if (this.inCountIn) {
          this.inCountIn = false;
        } else {
          this.bar++;
          this.applyTrainer();
        }
      }
    }
  }

  applyTrainer() {
    const t = this.config.trainer;
    if (!t?.on || !t.every || this.bar % t.every !== 0) return;
    const bpm = this.config.bpm;
    if (bpm >= t.target) return;
    const next = Math.min(t.target, bpm + t.step);
    this.config = { ...this.config, bpm: next };
    this.emit({ type: 'bpm', bpm: next });
  }

  playClick(time, level, isSub) {
    const ctx = this.ctx;
    const sound = SOUNDS[this.config.sound] || SOUNDS.click;
    const freq = isSub ? sound.freqs[2] * 0.75 : sound.freqs[3 - level] || sound.freqs[2];
    const gain = isSub ? 0.28 : ACCENT_GAIN[level];
    const out = ctx.createGain();
    out.gain.setValueAtTime(gain, time);
    out.gain.exponentialRampToValueAtTime(0.0001, time + sound.decay);
    out.connect(ctx.destination);

    const osc = ctx.createOscillator();
    osc.type = sound.type;
    osc.frequency.setValueAtTime(freq, time);
    if (sound.drop) osc.frequency.exponentialRampToValueAtTime(freq * sound.drop, time + sound.decay);
    osc.connect(out);
    osc.start(time);
    osc.stop(time + sound.decay + 0.02);

    if (sound.overtone) {
      const over = ctx.createOscillator();
      const og = ctx.createGain();
      og.gain.value = 0.3;
      over.frequency.setValueAtTime(freq * sound.overtone, time);
      over.connect(og).connect(out);
      over.start(time);
      over.stop(time + sound.decay + 0.02);
    }
  }

  playVoice(time, beat, level) {
    const clip = this.voice.clips[this.config.voice]?.[beat];
    if (!clip) return;
    const src = this.ctx.createBufferSource();
    const gain = this.ctx.createGain();
    gain.gain.value = VOICE_GAIN[level];
    src.buffer = this.voice.buffer;
    src.connect(gain).connect(this.ctx.destination);
    src.start(time, clip[0], clip[1]);
  }

  // 울릴 시각이 지난 박을 화면에 알린다. 가려져 있던 동안 쌓인 박은 건너뛰고 가장 최근 박만 보낸다.
  flush() {
    const now = this.ctx.currentTime;
    let latest = null;
    while (this.queue.length && this.queue[0].time <= now) latest = this.queue.shift();
    if (latest) this.emit({ ...latest, voiceActive: this.voiceFits() });
  }

  // 프레임마다 flush (rAF가 멈추는 환경 대비로 schedule()에서도 flush한다)
  draw() {
    if (!this.playing) return;
    this.flush();
    this.raf = requestAnimationFrame(() => this.draw());
  }
}
